import React from 'react';

export interface JoiningSection {
    id: string;
    title: string;
    content: React.ReactNode | ((onNavigate: (page: string) => void) => React.ReactNode);
}

export const joiningSections: JoiningSection[] = [
    {
        id: 'why-taekwon-do',
        title: 'What Your Child Gains from Taekwon-Do',
        content: (
            <>
                <p>
                    As parents ourselves, we know how much thought goes into picking the right activity for your child — and how easy it is to wonder, a few months in, whether it's actually doing them any good. So here's an honest look at why we think Taekwon-Do is worth your child's time, and ours.
                </p>
                <br />
                <p>
                    <strong>Why a martial art?</strong><br />
                    Most martial arts share a handful of things in common: a clear progression of belts that gives your child real, tangible goals to work towards; the steady practice of self-discipline, focus and respect woven into every class; and a structured outlet for the energy children naturally have. The lessons that come out of it — listening properly, persevering when something is hard, treating others with respect — are the ones that quietly show up at school, at home, and in the playground.
                </p>
                <br />
                <p>
                    <strong>Why Taekwon-Do?</strong><br />
                    Taekwon-Do is a Korean martial art with a clear ethical code at its heart — the five tenets of courtesy, integrity, perseverance, self-control and indomitable spirit. These aren't slogans we recite and forget; they're the standard children are held to in every class, often by themselves. Within that there's real variety — patterns, sparring, self-defence, kicking and breaking — so your child finds the parts that speak to them, and grows in the parts that don't. Children and adults train side-by-side in the same club, and there's a wider community of gradings, association events and optional competitions for those who'd like them.
                </p>
                <div className="thinking-of-joining-benefits-grid">
                    <div className="thinking-of-joining-benefit-card">
                        <h3>Confidence</h3>
                        <p>Standing tall, speaking up and trying new things — qualities that show up well beyond the training hall.</p>
                    </div>
                    <div className="thinking-of-joining-benefit-card">
                        <h3>Focus &amp; Self-Discipline</h3>
                        <p>Learning to listen, concentrate and see things through — habits children quietly take with them into the rest of life.</p>
                    </div>
                    <div className="thinking-of-joining-benefit-card">
                        <h3>Practical Self-Protection</h3>
                        <p>Awareness, calm under pressure, and the tools to stay safe — taught alongside the responsibility of when not to use them.</p>
                    </div>
                    <div className="thinking-of-joining-benefit-card">
                        <h3>Fitness &amp; Coordination</h3>
                        <p>A full-body activity with kicking, balance and agility — children are exercising without ever feeling like they are.</p>
                    </div>
                    <div className="thinking-of-joining-benefit-card">
                        <h3>Respect &amp; Good Values</h3>
                        <p>The five tenets — courtesy, integrity, perseverance, self-control and indomitable spirit — woven into every class.</p>
                    </div>
                    <div className="thinking-of-joining-benefit-card">
                        <h3>Belonging</h3>
                        <p>A welcoming, mixed-age community where children make friends across year groups and find their place.</p>
                    </div>
                </div>
                <br />
                <p>
                    <strong>Sticking with it.</strong><br />
                    There will be moments your child wants to give up. Gradings can be challenging, and not every student passes first time — that's part of the path, not the end of it. The children who push through those harder moments are almost always the ones who, later, are most glad they didn't quit.
                </p>
                <br />
                <p>
                    If your child is curious, we'd love to have them come and try a class — the trial is free, and there's no commitment beyond seeing whether Taekwon-Do is right for them.
                </p>
            </>
        ),
    },
    {
        id: 'trial-classes',
        title: 'Trial Classes',
        content: (
            <>
                <p>
                    We offer trial classes for any prospective new student, so that they are able to come and join in with us, start to meet new people and to have enough time for us to be able to show many of the elements that make up this fantastic sport.
                </p>
                <br />
                <div className="photo-block">
                    <img className="inline-photo-right" src="/images/general/protect-them.jpg" alt="Teach them to protect themselves" />
                    <p>
                        We find that one class is just not enough time, especially if the prospective student is perhaps a little more shy and timid or may have additional needs that we can help to enhance, to give the student the best experience possible in a safe and fun environment.
                    </p>
                    <br />
                    <p>
                        There have been so much positive feedback on the benefits of taking part in a Martial Art, with the likes of Taekwon-Do and Karate probably being the most common now.
                    </p>
                </div>
                <div className="photo-block">
                    <img className="inline-photo-left" src="/images/general/new-term.jpg" alt="New Term — time to get training!" />
                    <p>
                        It is not true that you need to be super flexible, be able to jump almost 3 metres and be strong enough to do 1000 push-ups. Especially from day 1 :)
                        <br />
                        Learning Taekwon-Do takes a long time, there is so much to learn and there is something for everyone to experience.
                    </p>
                    <br />
                    <p>
                        Our classes are designed to give a mix of all aspects and develop good all-round skills and abilities. Should a student then wish to specialise more in one area over another then we can accommodate and help with this.
                    </p>
                </div>
                <p>
                    Before a prospective student can start a trial, we would need the <strong>form</strong> signed and sent back to us, along with payment. (Both could be at the start of the first class).
                </p>
                <br />
                <p>
                    Here are a few things that have been said about the benefits of learning Taekwon-Do:
                </p>
                <div className="benefits-images">
                    <img src="/images/benefits-of-taekwon-do.jpg" alt="Benefits of Taekwon-Do" />
                    <img src="/images/reasons-for-children.jpg" alt="10 Good Reasons Why Children Should Learn Taekwon-Do" />
                </div>
                <img className="quote-image" src="/images/general/tkd-girls-quote.jpg" alt="To Taekwondo Girls Everywhere" />
            </>
        ),
    },
    {
        id: 'pricing',
        title: 'Pricing',
        content: (
            <>
                <p>
                    <strong>Free 2-session trial to both children and seniors</strong>
                </p>
                <br />
                <p>
                    <strong>Children's class</strong><br />
                    1 session per week - £35 pcm or £100 per term
                </p>
                <br />
                <p>
                    <strong>Seniors class</strong><br />
                    1 session per week - £40 pcm<br />
                </p>
                <br />
                <p>
                    We have "Starter Packs” which include uniform, annual membership and first month training fees (or calculated if mid-term) from £100
                </p>
            </>
        ),
    },
    // {
    //     id: 'membership-fees',
    //     title: 'Membership Fees',
    //     content: (
    //         <p>
    //             Please contact us for our current membership rates and family discounts.
    //         </p>
    //     ),
    // },
    {
        id: 'training-locations',
        title: 'Training Location',
        content: (
            <>
                <p>
                    <strong>Saturday</strong><br />
                    09:30 – 11:30<br />
                    WoodChurch, Scout and Guide Community Hall, Front Load, Woodchurch, TN26 3SQ
                </p>
            </>
        ),
    },
    {
        id: 'faqs',
        title: 'FAQs',
        content: (
            <>
                <p>
                    <strong>What do I need to wear or bring to my first class?</strong><br />
                    Something comfortable such as trackies or leggings and a T-shirt or similar. A bottle of water is always a good idea too.
                </p>
                <br />
                <p>
                    <strong>Will the lesson be hard?</strong><br />
                    We let you go at your own pace, we don't suggest you try to keep up with other students or try and better them, there's plenty of opportunity to become more competitive later, should you have the desire.
                </p>
                <br />
                <p>
                    <strong>I'm not very fit or strong, will this matter?</strong><br />
                    No, not at all. You will develop over time.
                </p>
                <br />
                <p>
                    <strong>I am not at all flexible, can I still do Taekwon-Do?</strong><br />
                    Yes of course! Some people are blessed with flexibility, some just have to work really hard and can become more flexible. Some students may not see any great improvement, it's partly down to your body. You can still achieve your belts and win medals without being flexible.
                </p>
                <br />
                <p>
                    <strong>Do you cater for students with a disability or learning difficulties?</strong><br />
                    Yes we do. We do our utmost to work with each student and the parents / guardians, etc to make the classes as open to everyone as we possibly can. We don't pretend that we have experience of helping in every area but we do listen and learn and try to adapt our classes where at all possible.
                </p>
                <br />
                <p>
                    <strong>Do I need to have done something like this before?</strong><br />
                    Not at all. We take complete novices.
                </p>
                <br />
                <p>
                    <strong>Is Taekwon-Do safe?</strong><br />
                    From experience, yes! We are a contact sport in general terms but we don't always go around kicking and punching each other.<br />
                    A lot of the work is done with pads or protective equipment. In classes, we keep any contact to a minimum and appropriate for the need of the exercise or learning of the technique.<br />
                    There are competitions that students can enter, to gain more experience and in the vast majority of these, the contact is still considered light.<br />
                    Of course, it would be inappropriate to say that there are no injuries, sometimes we fall over, make an error or do a bad technique and accidents do happen. Taekwon-Do does not have anything like the number of injuries seen in Football or Rugby though.
                </p>
                <br />
                <p>
                    <strong>Is there a maximum age? Am I too old?</strong><br />
                    No to both. If you want to take part then we are happy to have you. We know of students in their late 60's who are still competing in major events.
                </p>
                <br />
                <p>
                    <strong>What happens after the Trial Period?</strong><br />
                    We will have been talking with you and/or with parents and guardians as your trial progressed. The process is as much about whether you think Taekwon-Do is for you, if our club is right for you and to a lesser extent - if you are right for us. We don't want to teach people to go out and beat people up after all!
                </p>
                <br />
                <p>
                    <strong>Will my Son / Daughter learn anything other than how to kick and punch?</strong><br />
                    Absolutely yes! Whilst this is a Martial Art, there is so much more to this, Take a look at the Trial Classes page at the images for some of the core benefits to learning a Martial Art.<br />
                    We have had students arrive with zero confidence and in a short time are then able to stand up and perform in front of the class or even their own school.<br />
                    We have others who have been surprisingly uncoordinated and then go on and become Black Belts.<br />
                    Some students come to us due to a host of reasons and as a class unit, we all try and help one another to become better.
                </p>
                <br />
                <p>
                    <strong>If I decide this is for me, what happens next?</strong><br />
                    We will talk you through joining us, go through your initial goals (if you have any), get your Dobok (training suit) and belt ordered and get you registered with the association.
                </p>
                <br />
                <p>
                    <strong>How quickly can I get to Black Belt?</strong><br />
                    Well this generally depends upon you. We have 3 gradings a year but not everyone will do all 3, sometimes you will have to wait until we believe that you are ready. How many times you train each week, how quickly you learn new things and become proficient at them, will all determine how long this could take.
                </p>
                <br />
                <p>
                    <strong>Once I get my Black Belt, what is there left for me to achieve?</strong><br />
                    This is just the start of another journey. There are 9 black belt grades, with many new moves and requirements to grasp.<br />
                    There are options to do more Umpiring at competitions, to be a Competitor and perhaps specialise in Sparring, Breaking or Special Techniques.<br />
                    You could go and open your own club, become an assistant instructor and help others.<br />
                    We have also been invited into the local Schools and Girl Guides clubs to deliver content for them.
                </p>
                <br />
                <p>
                    <strong>Can I achieve a Black Belt before I turn 18?</strong><br />
                    Yes you can. It depends how old you are when you start but we have just recently had a 14yr old and a 15yr old achieve their first Black Belt.
                </p>
            </>
        ),
    },
];
